import { motion } from 'framer-motion';
import { Trophy, Sparkles } from 'lucide-react';

/**
 * Calcule le niveau et l'XP nécessaire pour le suivant.
 * Système simple : 100 XP par niveau, +50 par palier supplémentaire.
 *  L1 = 0-100, L2 = 100-250, L3 = 250-450, L4 = 450-700, ...
 */
export function computeUserLevel(xp = 0) {
  let level = 1;
  let needed = 100;
  let acc = 0;
  while (xp >= acc + needed) {
    acc += needed;
    level += 1;
    needed += 50;
  }
  const inLevel = xp - acc;
  const pct = Math.min(100, Math.round((inLevel / needed) * 100));
  return { level, currentLevelXp: inLevel, neededForNext: needed, pct, totalXp: xp };
}

/**
 * Messages motivants basés sur l'XP cumulé / leçons complétées.
 * Bilingues — renvoie {icon, text_fr, text_en}.
 */
export function getMotivationalMessage(xp = 0, completedLessons = 0) {
  if (completedLessons >= 30) return { icon: '🌟', text_fr: "Tu maîtrises l'anglais courant. Tu peux soutenir une vraie conversation.", text_en: "You master everyday English. You can sustain a real conversation." };
  if (completedLessons >= 20) return { icon: '🚀', text_fr: "Tu as atteint le niveau intermédiaire. Tu comprends et te débrouilles seul.", text_en: "You've reached intermediate level. You understand and can manage on your own." };
  if (completedLessons >= 15) return { icon: '🔥', text_fr: "Tu peux maintenant raconter ta journée et tes projets en anglais.", text_en: "You can now tell your day and share your plans in English." };
  if (completedLessons >= 10) return { icon: '💬', text_fr: "Tu peux déjà tenir une conversation simple en anglais.", text_en: "You can already hold a simple conversation in English." };
  if (completedLessons >= 7)  return { icon: '🎯', text_fr: "Tu sais te présenter et parler de ta routine en anglais.", text_en: "You can introduce yourself and talk about your routine in English." };
  if (completedLessons >= 5)  return { icon: '✅', text_fr: "Tu peux déjà dire 10 phrases utiles en anglais.", text_en: "You can already say 10 useful sentences in English." };
  if (completedLessons >= 3)  return { icon: '👏', text_fr: "Tu connais les bases — salutations, alphabet, nombres.", text_en: "You know the basics — greetings, alphabet, numbers." };
  if (xp >= 30)               return { icon: '💪', text_fr: "Bon départ ! Continue à ce rythme.", text_en: "Great start! Keep up the pace." };
  return { icon: '🌱', text_fr: "Bienvenue ! Commence ta première leçon pour démarrer.", text_en: "Welcome! Start your first lesson to get going." };
}

/**
 * Composant de barre de progression du niveau utilisateur
 * affichée en haut du Dashboard.
 */
export default function LevelProgress({ xp = 0, completedLessons = 0, lang = 'fr' }) {
  const lv = computeUserLevel(xp);
  const motiv = getMotivationalMessage(xp, completedLessons);
  const levelWord = lang === 'en' ? 'Level' : 'Niveau';
  const towards   = lang === 'en' ? 'to Level' : 'vers Niveau';
  const motivText = lang === 'en' ? motiv.text_en : motiv.text_fr;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="level-progress"
      className="bg-gradient-to-r from-yellow-500/20 via-emerald-500/15 to-emerald-500/10 border border-yellow-400/30 rounded-2xl p-4 mb-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-slate-950 font-black shadow-lg shadow-yellow-500/30">
          {lv.level}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-bold text-yellow-200 flex items-center gap-1"><Trophy size={12} />{levelWord} {lv.level}</span>
            <span className="text-white/60">{lv.currentLevelXp} / {lv.neededForNext} XP {towards} {lv.level + 1}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden mt-1.5">
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${lv.pct}%` }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-yellow-400 via-emerald-400 to-yellow-300 rounded-full" />
          </div>
        </div>
      </div>
      <div className="text-sm text-emerald-200 flex items-center gap-2">
        <Sparkles size={14} className="text-yellow-300 flex-shrink-0" />
        <span><span className="text-2xl mr-1">{motiv.icon}</span>{motivText}</span>
      </div>
    </motion.div>
  );
}
